import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useMediaQuery } from 'react-responsive';
import { motion } from 'framer-motion';
import { transition1 } from '../transitions';
import { CursorContext } from '../context/CursorContext';
import LazyImage from './LazyImage';

const DynamicGallery = ({ folder }) => {
  const { mouseEnterHandler, mouseLeaveHandler } = useContext(CursorContext);

  const [model, setModel] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);

  const isMdOrLarger = useMediaQuery({ query: '(min-width: 768px)' });

  // Dynamically load all images from the given folder
  const images = useMemo(() => {
    try {
      const context = require.context('../img/projects', true, /\.(jpg|jpeg|png|gif)$/i);
      const folderImages = context
        .keys()
        .filter(key => key.includes(`/${folder}/`))
        .sort()
        .map(key => context(key));
      return folderImages.length > 0 ? folderImages : [];
    } catch (err) {
      console.error(`Failed to load images for folder: ${folder}`, err);
      return [];
    }
  }, [folder]);

  // Lightbox helpers
  const openGallery = (imgSrc, index) => {
    setSelectedImage(imgSrc);
    setCurrentIndex(index);
    setModel(true);
  };

  const closeGallery = () => {
    setSelectedImage(null);
    setCurrentIndex(null);
    setModel(false);
  };

  const showNextImage = () => {
    const newIndex = (currentIndex + 1) % images.length;
    setSelectedImage(images[newIndex]);
    setCurrentIndex(newIndex);
  };

  const showPreviousImage = () => {
    const newIndex = (currentIndex - 1 + images.length) % images.length;
    setSelectedImage(images[newIndex]);
    setCurrentIndex(newIndex);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!model) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') showNextImage();
      if (e.key === 'ArrowLeft') showPreviousImage();
      if (e.key === 'Escape') closeGallery();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [model, currentIndex]);

  if (images.length === 0) {
    return (
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={transition1}
        className='section overflow-scroll'
      >
        <div className='container mx-auto h-full flex items-center justify-center'>
          <p className='text-center'>No images found for "{folder}"</p>
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={transition1}
      className='section overflow-scroll'
    >
      {/* Masonry grid — matches Gallery.js layout exactly */}
      <div
        className='items-center px-6 md:px-16 pt-[100px] lg:pt-[140px] relative'
        onMouseEnter={mouseEnterHandler}
        onMouseLeave={mouseLeaveHandler}
      >
        <div className='lg:columns-3 md:columns-2 columns-1'>
          {images.map((imgSrc, index) => (
            <div key={index} onClick={() => openGallery(imgSrc, index)}>
              <LazyImage
                src={imgSrc}
                className='w-full py-2 cursor-pointer'
              />
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox — matches Gallery.js exactly */}
      {model && (
        <div
          className='fixed top-0 left-0 right-0 bottom-0 flex justify-center items-center bg-white bg-opacity-90 z-50'
          style={{ height: '100vh' }}
        >
          <div className='flex justify-center items-center'>
            {currentIndex > 0 && isMdOrLarger && (
              <button
                className='absolute left-1 md:left-3 lg:left-5 text-black text-3xl md:text-4xl font-primary focus:outline-none p-3'
                onClick={showPreviousImage}
              >
                &larr;
              </button>
            )}

            <img
              style={{ objectFit: 'contain', maxHeight: '90vh', maxWidth: '85vw' }}
              src={selectedImage}
              alt=''
            />

            {currentIndex < images.length - 1 && isMdOrLarger && (
              <button
                className='absolute right-1 md:right-3 lg:right-5 text-black text-3xl md:text-4xl font-primary focus:outline-none p-3'
                onClick={showNextImage}
              >
                &rarr;
              </button>
            )}
          </div>

          <button
            className='absolute left-2 md:left-3 lg:left-5 top-1 md:top-2 text-black text-5xl font-primary font-bold focus:outline-none p-3'
            onClick={closeGallery}
          >
            &times;
          </button>
        </div>
      )}

      <footer className='flex footer w-full justify-center items-center pt-16 pb-8'>
        <div className='text-sm text-primary'>Copyright © 2026</div>
      </footer>
    </motion.section>
  );
};

export default DynamicGallery;